/**
 * Simple Quick Shop Implementation
 * Direct and reliable approach without Web Components
 */

(function() {
  'use strict';

  // Wait for DOM
  document.addEventListener('DOMContentLoaded', function() {
    // console.log('Quick Shop: Initializing...');

    // Global state
    window.QuickShop = {
      isOpen: false,
      currentProduct: null,
      cache: {},

      // Initialize the quick shop
      init: function() {
        this.modal = document.getElementById('quick-shop-modal');
        this.overlay = document.querySelector('[data-quick-shop-overlay]');
        this.container = document.querySelector('.quick-shop-container');
        this.closeButton = document.querySelector('[data-quick-shop-close]');
        this.loading = document.querySelector('[data-quick-shop-loading]');
        this.productContainer = document.querySelector('[data-quick-shop-product]');

        if (!this.modal) {
          console.error('Quick Shop: Modal not found');
          return;
        }

        this.bindEvents();
        // console.log('Quick Shop: Ready');
      },

      // Bind all events
      bindEvents: function() {
        const self = this;

        // Close button
        if (this.closeButton) {
          this.closeButton.addEventListener('click', function() {
            self.hide();
          });
        }

        // Backdrop click (::backdrop on <dialog>)
        this.modal.addEventListener('click', function(e) {
          if (e.target === self.modal) self.hide();
        });

        // ESC key — dialog fires 'cancel' natively; also handle keydown for safety
        this.modal.addEventListener('cancel', function(e) {
          e.preventDefault();
          self.hide();
        });
        document.addEventListener('keydown', function(e) {
          if (e.key === 'Escape' && self.isOpen) {
            self.hide();
          }
        });

        // Global click handler for trigger buttons
        document.addEventListener('click', function(e) {
          const trigger = e.target.closest('[data-quick-shop-trigger]');
          if (trigger) {
            e.preventDefault();
            e.stopPropagation();

            const handle = trigger.dataset.productHandle;
            const id = trigger.dataset.productId;

            // console.log('Quick Shop: Trigger clicked', handle);

            if (handle) {
              self.show(handle, id, trigger.dataset.preselectSize);
            }
          }
        });

        /* Close on a successful add from the shared buy buttons.
           assets/pdp-buy-actions.js only does the buying — it dispatches
           `cart:update` and stops there, exactly as it does on the PDP. Closing
           this modal is the modal's own business, so it lives here.

           Without it the dialog would stay in the browser's top layer, sitting on
           top of the cart drawer that cart-v2-drawer.js opens off the same event.

           Filtered on sourceId so an unrelated cart:update (a quantity change in
           the drawer, say) can't close the modal out from under the shopper. */
        document.addEventListener('cart:update', function(e) {
          if (!self.isOpen) return;
          if (e.detail && e.detail.sourceId === 'pdp-buy-actions') self.hide();
        });
      },

      // Show modal. preselectSize: a size value to pick when it is in stock.
      show: function(handle, id, preselectSize) {
        if (this.isOpen) return;
        this.preselectSize = preselectSize || null;

        // console.log('Quick Shop: Opening for', handle);

        this.isOpen = true;
        this.currentProduct = handle;
        this.currentProductId = id || null;

        // Lock background scroll without position:fixed (no scroll jump)
        document.documentElement.style.overflow = 'hidden';
        document.body.style.overflow = 'hidden';
        this.modal.showModal();

        // Redirect auto-focus from the close button to the dialog itself
        // so mouse-opened modals don't show a focus ring on the close icon.
        this.modal.focus({ preventScroll: true });

        // Show loading
        if (this.loading) {
          this.loading.style.display = 'flex';
        }
        if (this.productContainer) {
          this.productContainer.style.display = 'none';
          this.productContainer.innerHTML = '';
        }

        // Check cache
        if (this.cache[handle]) {
          // console.log('Quick Shop: Using cached data');
          this.renderProduct(this.cache[handle]);
        } else {
          // Fetch product
          this.fetchProduct(handle);
        }
      },

      // Hide modal
      hide: function() {
        if (!this.isOpen) return;

        // console.log('Quick Shop: Closing');

        this.isOpen = false;
        this.modal.close();
        document.documentElement.style.overflow = '';
        document.body.style.overflow = '';

        // Clear content after CSS hide (no inline style — class handles visibility)
        setTimeout(() => {
          if (this.productContainer) {
            this.productContainer.innerHTML = '';
          }
        }, 300);

        this.currentProduct = null;
        this.currentProductId = null;
      },

      // Fetch product data
      fetchProduct: function(handle) {
        const self = this;

        // console.log('Quick Shop: Fetching', handle);

        fetch('/products/' + handle + '?view=quick-shop', {
          headers: {
            'X-Requested-With': 'XMLHttpRequest'
          }
        })
        .then(function(response) {
          if (!response.ok) throw new Error('Failed to load product');
          return response.text();
        })
        .then(function(html) {
          // console.log('Quick Shop: Product loaded');
          self.cache[handle] = html;
          self.renderProduct(html);
        })
        .catch(function(error) {
          console.error('Quick Shop: Error', error);
          self.showError();
        });
      },

      // Render product content
      renderProduct: function(html) {
        if (!this.productContainer) return;

        // Hide loading
        if (this.loading) {
          this.loading.style.display = 'none';
        }

        // Show content
        this.productContainer.innerHTML = html;
        this.productContainer.style.display = 'block';

        // Initialize form
        this.initializeForm();

        // Pick the requested size (the bag's reco rows pass the anchor's size).
        if (this.preselectSize) {
          const want = this.preselectSize;
          const pick = Array.from(this.productContainer.querySelectorAll('.qs-size-option:not(.sold-out)')).find(function(o) {
            return (o.dataset.optionName || '').toLowerCase() === 'size' && o.dataset.optionValue === want;
          });
          if (pick) pick.click();
        }

        // console.log('Quick Shop: Product rendered');
        document.dispatchEvent(new CustomEvent('quickshop:product-rendered', {
          detail: { handle: this.currentProduct }
        }));
      },

      // Initialize product form
      initializeForm: function() {
        const form = this.productContainer.querySelector('[data-product-form]');

        if (!form) return;

        /* Add to Bag moved to assets/pdp-buy-actions.js, shared with the product
           page. It goes through cartService.addItem and dispatches `cart:update`,
           the same as the PDP.

           Note this drops the old `quickshop:added-to-cart` event, so the wishlist
           Move-to-Bag flow no longer auto-removes the item — accepted tradeoff for
           having one shared buy path. The listeners are in
           wishlist-controller_v2.js, wishlist_section_v2.liquid and
           cart-drawer-wishlist_v2.liquid if that needs revisiting. */

        /* Colour swatches and option pills (size, age …). The variant comes from
           EVERY selected option: a pill's own data-variant-id is only the first
           variant with that value, whatever its colour. Picking a colour also
           re-marks which pills that colour has in stock. */
        let data = null;
        try {
          const raw = JSON.parse(this.productContainer.querySelector('[data-qs-variants]')?.textContent || 'null');
          if (raw) data = {
            options: raw.options,
            variants: raw.ids.map(function(id, i) { return { id: id, available: raw.available[i], options: raw.values[i] }; })
          };
        } catch (e) {
          data = null;
        }
        const choices = () => this.productContainer.querySelectorAll('.qs-color-swatch, .qs-size-option');
        const groupOf = (el) => el.closest('.qs-colors, .qs-sizes') || el.parentElement;

        // Variants matching the selected options, optionally with one option swapped.
        const matches = (override) => {
          if (!data) return [];
          const picked = {};
          this.productContainer.querySelectorAll('.qs-color-swatch.selected, .qs-size-option.selected').forEach(function(el) {
            picked[el.dataset.optionName] = el.dataset.optionValue;
          });
          if (override) picked[override.dataset.optionName] = override.dataset.optionValue;
          return data.variants.filter(function(v) {
            return data.options.every(function(name, i) {
              return !(name in picked) || v.options[i] === picked[name];
            });
          });
        };

        // More than one option shown (colour × size, style × size): pills depend on the other choice.
        const multi = this.productContainer.querySelectorAll('.qs-colors, .qs-sizes').length > 1;

        const sync = () => {
          if (!data) return;
          // Pills: in stock with the other current choices?
          this.productContainer.querySelectorAll('.qs-size-option').forEach(function(pill) {
            const ok = matches(pill).some(function(v) { return v.available; });
            pill.disabled = !ok;
            pill.classList.toggle('sold-out', !ok);
            if (multi) {
              // ponytail: Liquid's "Only N left" and fast mark describe the first
              // variant with this value, whatever its colour; dropped here rather
              // than shipping per-variant stock. "Sold out" follows the pill.
              pill.classList.remove('qs-size-option--fast');
              pill.querySelector('.qs-size-fast-icon')?.remove();
              const item = pill.closest('.qs-pill-item');
              item?.querySelector('.qs-stock')?.remove();
              if (!ok && item) item.insertAdjacentHTML('beforeend', '<span class="qs-stock qs-stock--out">Sold out</span>');
            }
          });
          // A selected pill the new colour doesn't have moves to the first one it does.
          this.productContainer.querySelectorAll('.qs-sizes').forEach(function(group) {
            const sel = group.querySelector('.qs-size-option.selected');
            if (sel && sel.disabled) {
              sel.classList.remove('selected');
              const next = group.querySelector('.qs-size-option:not(:disabled)');
              if (next) next.classList.add('selected');
            }
          });

          const variant = matches().find(function(v) { return v.available; }) || matches()[0];
          if (!variant) return;
          const variantInput = this.productContainer.querySelector('[data-variant-input]');
          if (variantInput) variantInput.value = variant.id;

          /* Keep the shared buy buttons in sync. On the PDP they pick this up
             from the `variant:change` event that custom-variant-selector fires;
             the mini PDP has no variant selector, so it writes the id straight
             onto the wrapper that assets/pdp-buy-actions.js reads. */
          const buyActions = this.productContainer.querySelector('.pdp-buy-actions');
          if (buyActions) {
            buyActions.dataset.pdpVariantId = variant.id;
            setPdpBuyActionsAvailable(buyActions, variant.available);
          }
        };

        choices().forEach(function(option) {
          option.addEventListener('click', function() {
            if (option.disabled) return;
            groupOf(option).querySelectorAll('.selected').forEach(s => s.classList.remove('selected'));
            option.classList.add('selected');
            sync();
          });
        });
        sync();

        /* Buy Now moved to assets/pdp-buy-actions.js, shared with the product
           page. It is driven by the shared snippet's own hidden Fastrr form, so
           there is nothing to bind here. */

        // Quantity selector (if present)
        const minusBtn = this.productContainer.querySelector('[data-quantity-minus]');
        const plusBtn = this.productContainer.querySelector('[data-quantity-plus]');
        const qtyInput = this.productContainer.querySelector('[data-quantity-input]');

        if (minusBtn && plusBtn && qtyInput) {
          minusBtn.addEventListener('click', function() {
            const current = parseInt(qtyInput.value) || 1;
            if (current > 1) {
              qtyInput.value = current - 1;
            }
          });

          plusBtn.addEventListener('click', function() {
            const current = parseInt(qtyInput.value) || 1;
            qtyInput.value = current + 1;
          });
        }
      },

      // Show error
      showError: function() {
        if (this.productContainer) {
          this.productContainer.innerHTML = `
            <div class="quick-shop-error" style="padding: 40px; text-align: center;">
              <p style="color: #EF4444; margin-bottom: 20px;">Failed to load product</p>
              <button onclick="QuickShop.hide()" style="padding: 10px 20px; background: #F26216; color: white; border: none; border-radius: 6px; cursor: pointer;">Close</button>
            </div>
          `;
          this.productContainer.style.display = 'block';
        }
        if (this.loading) {
          this.loading.style.display = 'none';
        }
      },

      // Refresh cart count badge in header
      refreshCartCount: function() {
        fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
        .then(function(response) { return response.json(); })
        .then(function(cart) {
          // Update cart bubble visibility and count
          document.querySelectorAll('.cart-bubble').forEach(function(bubble) {
            if (cart.item_count > 0) {
              bubble.classList.remove('visually-hidden');
            } else {
              bubble.classList.add('visually-hidden');
            }
          });
          document.querySelectorAll('.cart-bubble__text-count').forEach(function(el) {
            el.textContent = cart.item_count;
            el.classList.toggle('hidden', cart.item_count === 0);
          });
        })
        .catch(function(error) {
          console.error('Quick Shop: Error refreshing cart count', error);
        });
      }
    };

    // Initialize
    window.QuickShop.init();
  });
})();